import {
  ActionIcon,
  Box,
  Button,
  Center,
  createStyles,
  Group,
  Menu,
  Modal,
  Stack,
  Text,
  TextInput,
  useMantineTheme,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import React, { useEffect, useState } from 'react';
import { Dots, Pencil, Trash, X } from 'tabler-icons-react';
import { useMediaQuery } from '@mantine/hooks';
import { IEquipmentType } from '../../../models';
import { useDeleteEquipmentType, useEditEquipmentType, useUserId } from '../../../hooks';
import { useModals } from '@mantine/modals';
import { DialogModal } from '../..';

const useStyles = createStyles((theme) => ({
  label: {
    fontSize: theme.fontSizes.xs,
    whiteSpace: 'pre',
    fontWeight: 500,
  },
  fieldGroup: {
    width: '48%',
  },
  detailLabel: {
    color: theme.colors.gray[5],
    fontSize: theme.fontSizes.xs,
    whiteSpace: 'pre',
  },
  detail: {
    fontSize: theme.fontSizes.md,
    paddingLeft: 10,
  },
  buttonText: {
    [`@media (max-width: ${theme.breakpoints.sm})`]: {
      fontSize: 12,
    },
  },
}));

type EquipmentTypeEditModalProps = {
  equipmentType: IEquipmentType;
  opened: boolean;
  onClose: () => void;
  onEdited: (isUpdated: boolean, isError: boolean, initialEquipment: IEquipmentType) => void;
  onDeleted: () => void;
};

const EquipmentTypeEditModal = (props: EquipmentTypeEditModalProps) => {
  const { classes } = useStyles();
  const theme = useMantineTheme();
  const modals = useModals();
  const isSmallMobile = useMediaQuery(`(max-width: ${theme.breakpoints.xs})`);

  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);

  const [isError, setIsError] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');

  const initialEquipment = {
    equipmentTypeId: props.equipmentType.equipmentTypeId,
    name: props.equipmentType.name,
    lastUpdatedUserId: props.equipmentType.lastUpdatedUserId,
    lastUpdatedDateTime: props.equipmentType.lastUpdatedDateTime,
  };

  const currentUserId = useUserId();

  const [submitClicked, setSubmitClicked] = useState<boolean>(false);

  const editEquipmentType = useEditEquipmentType();

  const deleteEquipmentType = useDeleteEquipmentType();

  const handleDelete = () => {
    deleteEquipmentType.mutate({ equipmentTypeId: props.equipmentType.equipmentTypeId });
  };

  const editForm = useForm<IEquipmentType>({
    initialValues: initialEquipment,
    validate: {
      name: (a) => (a.length > 50 ? 'Category Name must be a maximum of 50 characters' : null),
    },
  });

  async function submitEquipmentUpdate() {
    setSubmitClicked(true);

    editEquipmentType.mutate({
      equipmentTypeId: editForm.values.equipmentTypeId,
      name: editForm.values.name,
      lastUpdatedUserId: currentUserId.data!.userId,
    });
  }

  useEffect(() => {
    editForm.setValues(props.equipmentType);
  }, [props.equipmentType]);

  useEffect(() => {
    if (submitClicked) {
      if (editEquipmentType.isSuccess) {
        setSubmitClicked(false);
        setIsError(false);
        setErrorMessage('');
        props.onEdited(true, false, {
          equipmentTypeId: props.equipmentType.equipmentTypeId,
          name: editForm.values.name,
          lastUpdatedUserId: currentUserId.data!.userId,
          lastUpdatedDateTime: new Date(),
        });
      } else if (editEquipmentType.isError) {
        setSubmitClicked(false);
        setIsError(false);
        setErrorMessage('');
        props.onEdited(false, true, initialEquipment);
      }
    }
  }, [editEquipmentType.status]);

  useEffect(() => {
    if (deleteEquipmentType.isSuccess) {
      setIsError(false);
      setErrorMessage('');
      props.onDeleted();
    }
    if (deleteEquipmentType.isError) {
      setIsError(true);
      setErrorMessage('Something went wrong, Category not Deleted. Please try again');
    }
  }, [deleteEquipmentType.status]);

  return (
    <Modal
      title={isError ? <Text color="red">{errorMessage}</Text> : ''}
      opened={props.opened}
      size={isSmallMobile ? 'xs' : 'md'}
      onClose={() => {
        setIsError(false);
        setErrorMessage('');
        props.onClose();
      }}
    >
      <Box>
        <Center>
          <Group noWrap spacing={!isSmallMobile ? 'md' : 0} ml={!isSmallMobile ? 25 : 0}>
            <Text size="xl" weight={500} align="center">
              Edit Equipment Category Information
            </Text>
            <Menu>
              <Menu.Target>
                <ActionIcon>
                  <Dots />
                </ActionIcon>
              </Menu.Target>
              <Menu.Dropdown>
                <Menu.Item
                  icon={<Trash color={`${theme.colors.red[6]}`} />}
                  onClick={() => {
                    setDeleteModalOpen(true);
                  }}
                >
                  Delete Category
                </Menu.Item>
              </Menu.Dropdown>
            </Menu>
          </Group>
        </Center>
        <form onSubmit={editForm.onSubmit(() => submitEquipmentUpdate())} autoComplete="off">
          <Stack align="stretch" style={{ padding: 5 }}>
            <Stack align="stretch" spacing={0}>
              <Text className={classes.label}>Equipment Name</Text>
              <TextInput placeholder="Category Name" required {...editForm.getInputProps('name')} />
            </Stack>

            <Group position="apart" grow noWrap mt="xs">
              <Button variant="outline" type="submit">
                <Pencil style={{ marginRight: 5 }} />{' '}
                <Text className={classes.buttonText}>{`Update ${
                  !isSmallMobile ? 'Category' : ''
                }`}</Text>
              </Button>
              <Button
                variant="outline"
                color="red"
                onClick={() => {
                  setIsError(false);
                  setErrorMessage('');
                  props.onClose();
                }}
              >
                <X style={{ marginRight: 5 }} /> <Text className={classes.buttonText}>Cancel</Text>
              </Button>
            </Group>
          </Stack>
        </form>
      </Box>
      <DialogModal
        isOpen={deleteModalOpen}
        isNegative
        titleText="Delete Equipment Category?"
        body={
          <Text align="center" color="dimmed" size="lg" mb="xs">
            Are you sure you want to delete this Category?
          </Text>
        }
        confirmText="Delete Category"
        onConfirm={() => {
          setDeleteModalOpen(false);
          handleDelete();
        }}
        onClose={() => {
          setDeleteModalOpen(false);
        }}
      />
    </Modal>
  );
};

export default EquipmentTypeEditModal;
